import React from 'react'
import { Button } from '@/components/common/Button'
import {
  COMMITMENT_ACTION_TYPES,
  type CommitmentActionType,
  type CreateCommitmentInput,
} from '@/hooks/useCommitments'

interface CommitmentPersonOption {
  id: string
  title: string
}
interface CommitmentResponsibleOption { id: string; title: string }

interface CommitmentFormProps {
  organizationId: string
  organizationName: string
  people?: CommitmentPersonOption[]
  selectedPersonId?: string | null
  selectedPersonName?: string | null
  responsibles?: CommitmentResponsibleOption[]
  onSave: (input: CreateCommitmentInput) => Promise<void>
  onCancel: () => void
}

function errorDetail(error: unknown): string {
  if (!(error instanceof Error) || !error.message) return 'Error no identificado.'
  return error.message
}

export const CommitmentForm: React.FC<CommitmentFormProps> = ({
  organizationId,
  organizationName,
  people = [],
  selectedPersonId = null,
  selectedPersonName = null,
  responsibles = [],
  onSave,
  onCancel,
}) => {
  const [actionType, setActionType] = React.useState<CommitmentActionType | ''>('')
  const [personId, setPersonId] = React.useState(selectedPersonId ?? '')
  const [dueDate, setDueDate] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [responsableId, setResponsableId] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!actionType) return setError('Selecciona un tipo de acción.')
    if (!dueDate) return setError('Selecciona la fecha del compromiso.')

    setSaving(true)
    setError(null)
    try {
      await onSave({
        title: actionType,
        organizacionId: organizationId,
        personaId: personId || null,
        responsableId: responsableId || null,
        vencimiento: dueDate,
        descripcion: description.trim() || null,
      })
    } catch (saveError) {
      setError(`No se confirmó el guardado del compromiso. Detalle: ${errorDetail(saveError)}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="prototypeCommitmentForm" onSubmit={submit}>
      <p className="prototypeRelationshipRow__summary">
        Nada se guardará hasta que presiones “Guardar compromiso”.
      </p>
      <div className="prototypeRelationshipRow__grid">
        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Empresa</span>
          <input type="text" value={organizationName} readOnly />
        </label>

        {responsibles.length > 0 && <label className="prototypeRelationshipRow__cell"><span className="prototypeMiniLabel">Responsable</span><select value={responsableId} onChange={(event) => setResponsableId(event.target.value)}><option value="">Responsable de empresa</option>{responsibles.map((responsible) => <option key={responsible.id} value={responsible.id}>{responsible.title}</option>)}</select></label>}

        {selectedPersonId ? (
          <label className="prototypeRelationshipRow__cell">
            <span className="prototypeMiniLabel">Persona</span>
            <input type="text" value={selectedPersonName ?? 'Persona seleccionada'} readOnly />
          </label>
        ) : (
          <label className="prototypeRelationshipRow__cell">
            <span className="prototypeMiniLabel">Persona (opcional)</span>
            <select value={personId} onChange={(event) => setPersonId(event.target.value)}>
              <option value="">Sin persona específica</option>
              {people.map((person) => <option key={person.id} value={person.id}>{person.title}</option>)}
            </select>
          </label>
        )}

        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Tipo de acción *</span>
          <select value={actionType} onChange={(event) => setActionType(event.target.value as CommitmentActionType | '')} required>
            <option value="">Seleccionar…</option>
            {COMMITMENT_ACTION_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </label>

        <label className="prototypeRelationshipRow__cell">
          <span className="prototypeMiniLabel">Fecha compromiso *</span>
          <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} required />
        </label>

        <label className="prototypeRelationshipRow__cell" style={{ gridColumn: '1 / -1' }}>
          <span className="prototypeMiniLabel">Descripción breve</span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            placeholder="Contexto necesario para ejecutar la próxima acción"
          />
        </label>
      </div>

      {error && <p className="prototypeCommitmentForm__error" role="alert">{error}</p>}
      <div className="prototypeContextCard__actions">
        <Button size="sm" type="submit" disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar compromiso'}
        </Button>
        <Button size="sm" type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
      </div>
    </form>
  )
}
